import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { ContactApiModel, EmploymentApiModel, PrivateContactMetadataApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import {
    BaseJournodexEntityModel,
    FRESHNESS_THRESHOLD_MS,
    VERIFIED_THRESHOLD_MS,
    VETERAN_THRESHOLD_MS } from '../../../Discover/Targeting/models/base-journodex-entity.model';
import { EmploymentModel } from '../../../Discover/Targeting/models/employment.model';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';
import { HandleModel } from '../../../Discover/Targeting/models/handle.model';
import { UserNoteModel } from '../../../Common/Misc/models/user-note.model';
import { SubjectUtils } from '../../../Common/Journodex/services/journodex-subjects.service';
import { HandleTypeEnum } from '../../../api/targeting/enum/handle-type.enum';

export class PrivateContactMetadataModel {
    createdUserId?: number;
    modifiedUserId?: number;
    modifiedDate?: Date;
    createdDate?: Date;
    outletName?: string;

    constructor(apiModel: PrivateContactMetadataApiModel) {
        if (apiModel) {
            this.createdUserId = apiModel.createdUserId;
            this.modifiedUserId = apiModel.modifiedUserId;
            if (apiModel.createdDate) {
                this.createdDate = new Date(apiModel.createdDate);
            }
            if (apiModel.modifiedDate) {
                this.modifiedDate = new Date(apiModel.modifiedDate);
            }
            this.outletName = apiModel.outletName;
        }
    }

    clone() {
        const clone = new PrivateContactMetadataModel(null);
        clone.createdUserId = this.createdUserId || null;
        clone.modifiedUserId = this.modifiedUserId || null;
        clone.createdDate = this.createdDate;
        clone.modifiedDate = this.modifiedDate;
        clone.outletName = this.outletName;
        return clone;
    }
}

export class ContactModel extends BaseJournodexEntityModel {

    firstName?: string;
    middleName?: string;
    lastName?: string;
    name?: string;

    employmentModels: EmploymentModel[];
    selectedEmployment: EmploymentModel;

    listMemberships: TargetingList[];

    // TODO : revisit if we need a dedicated model for this
    userNotes?: UserNoteModel[] = [];

    privateContactMetadata?: PrivateContactMetadataModel;

    // TODO : rest of fields
    private simpleCompareFields = [
        'firstName',
        'lastName',
        'addressLine1',
        'addressLine2',
        'postalCode',
        'bio'
    ];

    // TODO : rest of fields
    private arrayCompareFields = [
        'handles',
        // 'employmentModels', // Subject references causing circular stringifcation
        'languages',
        'userNotes'
    ];

    // quick search only
    outlet?: string;

    constructor(apiModel?: ContactApiModel, locale?: string) {
        super(apiModel, locale);
        this.entityType = EntityType.CONTACT;
        if (apiModel) {

            this.firstName = apiModel.firstName;
            this.middleName = apiModel.middleName;
            this.lastName = apiModel.lastName;

            if (apiModel.privateContactMetadata) {
                this.privateContactMetadata = new PrivateContactMetadataModel(apiModel.privateContactMetadata);
            }

            if (apiModel.handles) {
                this.handles = apiModel.handles.map(handle => new HandleModel(handle, false, this.entityType));
                const email = this.handles.find(x => x.primary && x.handleType === HandleTypeEnum.EMAIL);
                this.primaryEmail = !!email ? email.value : null;
            }

            if (apiModel.userNotes) {
                this.userNotes = apiModel.userNotes.map(x => new UserNoteModel(x));
                this.userNotes.sort((x1, x2) => x2.creationDate.getTime() - x1.creationDate.getTime());
            }

            this.setBadges(apiModel);
        }
    }

    toApiModel(): ContactApiModel {
        let employments = [];
        if (this.employmentModels?.length) {
            employments = this.employmentModels.map(x => x.toApiModel());
        } else if (
            !!this.selectedEmployment &&
            (this.selectedEmployment.id || this.selectedEmployment.jobTitle || this.selectedEmployment.jobRoles.length > 0 || this.selectedEmployment.outlet)
        ) {
            employments = [ this.selectedEmployment.toApiModel() ];
        }

        return {
            id: this.id,
            firstName: this.firstName?.trim(),
            middleName: this.middleName?.trim(),
            lastName: this.lastName?.trim(),
            handles: this.handles.map(handle => handle.toApiModel()),
            addressLine1: this.addressLine1?.trim(),
            addressLine2: this.addressLine2?.trim(),
            postalCode: this.postalCode?.trim(),
            bio: this.bio,
            geography: !!this.geography ? {
                id : this.geography.collectionId
            } : null,
            subjects: SubjectUtils.convertParentSubjectArrayToApi(this.subjects),
            languages: this.languages.map(language => language.apiModel ?? language.id),
            employments: employments,
            userNotes: !!this.userNotes ? this.userNotes.map(x => x.toApiModel()) : [],
            avatarUrl: this.avatarUrl,
            isGeoRestricted: this.isGeoRestricted,
        };
    }

    // note : changes in the order of the handles will count as "dirt"
    equals(other: ContactModel): boolean {
        if (!other) {
            return false;
        }

        // TODO: Revist how we compare all fields in this function, should be a more
        // succinct/efficient way to deeply compare objects and fields

        for (const field of this.simpleCompareFields) {
            // Ensure that at least one has content to compare
            // '', null, and undefined are all equal here
            const thisIsTruthy = !!this[field];
            const otherIsTruthy = !!other[field];
            if (
                (thisIsTruthy || otherIsTruthy)
                && this[field] !== other[field]
            ) {
                return false;
            }
        }

        // Order invariant equality
        for (const field of this.arrayCompareFields) {
            const left: any[] | null = this[field] ?? null;
            const right: any[] | null = other[field] ?? null;
            if (left && left.length === right.length) {
                for (const element of left) {
                    if (!right.find(x =>
                        JSON.stringify(x, this.emptyToNullReplacer) === JSON.stringify(element, this.emptyToNullReplacer)
                    )) {
                        return false;
                    }
                }
            } else {
                return false;
            }
        }

        // Subjects can't be stringified due to circular ref... have to do it manually
        // TODO: Better compare
        const leftSubjects = this.subjects ? this.subjects : [];
        const rightSubjects = other.subjects ? other.subjects : [];
        if (leftSubjects.length === rightSubjects.length) {
            for (const element of leftSubjects) {
                if (!rightSubjects.find(x => element.id === x.id)) {
                    return false;
                }
            }
        } else {
            return false;
        }

        const thisSelectedEmployment = this.selectedEmployment ?? null;
        const otherSelectedEmployment = other.selectedEmployment ?? null;
        if (
            thisSelectedEmployment !== otherSelectedEmployment
            || !thisSelectedEmployment || !otherSelectedEmployment
            || (
                JSON.stringify(thisSelectedEmployment.outlet, this.emptyToNullReplacer)
                !== JSON.stringify(otherSelectedEmployment.outlet, this.emptyToNullReplacer)
            )
        ) {
            return false;
        }

        const thisJobTitle = this.selectedEmployment?.jobTitle ?? '';
        const otherJobTitle = other.selectedEmployment?.jobTitle ?? '';
        if (thisJobTitle !== otherJobTitle) {
            return false;
        }

        const thisJobRoles: any[] = this.selectedEmployment?.jobRoles ?? [];
        const otherJobRoles: any[] = other.selectedEmployment?.jobRoles ?? [];
        if (thisJobRoles.length === otherJobRoles.length) {
            for (const element of thisJobRoles) {
                if (!otherJobRoles.find(x =>
                    JSON.stringify(x, this.emptyToNullReplacer) === JSON.stringify(element, this.emptyToNullReplacer)
                )) {
                    return false;
                }
            }
        } else {
            return false;
        }

        // TODO: Review this logic. Quick and dirty way of comparing geographies for now
        if (this.geography || other.geography) {
            return this.geography?.collectionId === other.geography?.collectionId;
        }

        return true;
    }

    clone(): ContactModel {
        const clone =  new ContactModel(this.toApiModel());
        clone.subjects = this.subjects.slice();
        clone.selectedEmployment = this.selectedEmployment?.clone();
        clone.employmentModels = this.employmentModels?.map(employment => employment?.clone());
        clone.badges = this.badges?.slice();
        clone.privateContactMetadata = this.privateContactMetadata ? this.privateContactMetadata.clone() : null;
        clone.geography = this.geography;

        return clone;
    }

    setBadges(apiModel: ContactApiModel) {
        this.badges = [];

        if (this.dateDeltaToNow(apiModel.creationDate) < FRESHNESS_THRESHOLD_MS) {
            this.badges.push({
                iconClass: 'agpr-i-badge-new',
                tooltip: 'Discover.badges.tooltips.' + EntityType.CONTACT.toLowerCase() + '.new'
            });
        }

        // why doesnt contact end point return modified date?
        if (apiModel.modifiedDate) {
            if (this.dateDeltaToNow(apiModel.modifiedDate) < FRESHNESS_THRESHOLD_MS) {
                this.badges.push({
                    iconClass: 'fa-solid fa-sparkle',
                    tooltip: 'Discover.badges.tooltips.' + EntityType.CONTACT.toLowerCase() + '.recentlyUpdated'
                });
            }
        }
        if (
            apiModel.employments?.find(x => x.startDate && (this.dateDeltaToNow(x.startDate) < FRESHNESS_THRESHOLD_MS))
        ) {
            this.badges.push({
                iconClass: 'fa-solid fa-suitcase',
                tooltip: 'Discover.badges.tooltips.contact.newEmployment'
            });
        }
        if (apiModel.privateContactMetadata) {
            this.badges.push({
                iconClass: 'fa-solid fa-shield-keyhole',
                tooltip: 'Discover.badges.tooltips.contact.private'
            });
        }
        if (apiModel.employments?.length > 1) {
            this.badges.push({
                iconClass: 'fa-kit fa-agpr-employments',
                tooltip: 'Discover.badges.tooltips.contact.multipleEmployments'
            });
        }
        if (this.dateDeltaToNow(apiModel.creationDate) > VETERAN_THRESHOLD_MS) {
            this.badges.push({
                iconClass: 'fa-kit fa-agpr-ten-more',
                tooltip: 'Discover.badges.tooltips.' + EntityType.CONTACT.toLowerCase() + '.veteran'
            });
        }
        // Recently Verified badge hidden for now (10-1)
        // if (
        //     apiModel.verifiedDate
        //     && (this.dateDeltaToNow(apiModel.verifiedDate) < VERIFIED_THRESHOLD_MS)
        // ) {
        //     this.badges.push({
        //         iconClass: 'agpr-i-badge-verified',
        //         tooltip: 'Discover.badges.tooltips.contact.verified'
        //     });
        // }
    }

    /**
     * Used by stringify to alter the stringification. Replaces any empty (undefined, null, '') values
     * explicitly with null to standardize comparisons between objects
     */
    private emptyToNullReplacer(_, value): any {
        if (value === '' || value === undefined || value === null) {
            return null;
        }
        return value;
    }
}
